import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, UserMinus, ArrowRightLeft, Loader2, Star, Copy, Wand2 } from "lucide-react";
import { errMsg } from "./apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const selectCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-xs focus:outline-none focus:border-[#C5A059]";

export default function AdminEffectif() {
  const [regs, setRegs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [genre, setGenre] = useState("tous");
  const [categorie, setCategorie] = useState("toutes");
  const [recherche, setRecherche] = useState("");
  const [cibles, setCibles] = useState({});
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/registrations`, { withCredentials: true }),
      axios.get(`${API}/admin/teams`, { params: { detection_id: "kinshasa-2026" }, withCredentials: true }),
    ]).then(([r, t]) => {
      setRegs(r.data);
      setTeams(t.data);
      setLoading(false);
    }).catch(() => { toast.error("Erreur de chargement"); setLoading(false); });
  }, []);
  useEffect(load, [load]);

  const equipeDe = useMemo(() => {
    const map = {};
    for (const t of teams) for (const j of t.joueurs || []) map[j.registration_id] = t;
    return map;
  }, [teams]);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return regs.filter((r) => {
      if (r.paiement_statut !== "payée") return false; // les non-inscrits restent dans CRM / Inscriptions
      if (genre !== "tous" && (r.genre || "masculin") !== genre) return false;
      if (categorie !== "toutes") {
        const cat = (r.categorie_key || "").startsWith("u19") ? "U19" : "U23";
        if (cat !== categorie) return false;
      }
      if (q && !`${r.prenom} ${r.nom} ${r.reference || ""} ${r.email || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [regs, genre, categorie, recherche]);

  const placer = async (r) => {
    const target = cibles[r.id];
    const slot = slots[r.id];
    if (!target) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${target}/ajouter`, {
        registration_id: r.id,
        slot: slot || undefined,
      }, { withCredentials: true });
      toast.success(data.message);
      setCibles((c) => ({ ...c, [r.id]: "" }));
      setSlots((s) => ({ ...s, [r.id]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Placement impossible"));
    }
  };

  const retirer = async (r) => {
    const team = equipeDe[r.id];
    if (!team) return;
    if (!window.confirm(`Retirer ${r.prenom} ${r.nom} de ${team.nom} ? (son inscription est conservée)`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${team.id}/retirer`, { registration_id: r.id }, { withCredentials: true });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Retrait impossible"));
    }
  };

  const dupliquer = async (r) => {
    const team = equipeDe[r.id];
    const target = cibles[r.id];
    const slot = slots[r.id];
    if (!team || !target) return;
    try {
      const { data } = await axios.post(`${API}/admin/teams/${team.id}/dupliquer`, {
        registration_id: r.id,
        target_team_id: target,
        slot: slot || undefined,
      }, { withCredentials: true });
      toast.success(data.message);
      if (data.avertissement) toast.warning(data.avertissement, { duration: 8000 });
      setCibles((c) => ({ ...c, [r.id]: "" }));
      setSlots((s) => ({ ...s, [r.id]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e, "Duplication impossible"));
    }
  };

  const remplir = async () => {
    if (!window.confirm("Assigner automatiquement tous les joueurs confirmés sans équipe ? (poste principal puis secondaire, débordements en réservistes)")) return;
    try {
      const { data } = await axios.post(`${API}/admin/equipes/remplir`, {}, { withCredentials: true });
      toast.success(`${data.message} — ${data.assignes} joueur(s) placé(s), ${data.reservistes} réserviste(s)`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Remplissage impossible"));
    }
  };

  const toggleEtoile = async (r) => {
    const next = !r.selection_etoiles;
    try {
      await axios.patch(`${API}/admin/registrations/${r.id}`, { selection_etoiles: next }, { withCredentials: true });
      toast.success(next
        ? `${r.prenom} ${r.nom} retenu pour le Tournoi des Étoiles (21 nov) — notifié sur son portail`
        : `${r.prenom} ${r.nom} retiré de la sélection Étoiles`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action impossible"));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-[#C5A059]" /></div>;

  return (
    <div data-testid="admin-effectif" className="space-y-6">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un joueur…"
            data-testid="effectif-recherche"
            className={`${selectCls} pl-9 w-64`}
          />
        </div>
        <select value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="effectif-filtre-genre" className={selectCls}>
          <option value="tous">Garçons & Filles</option>
          <option value="masculin">Garçons</option>
          <option value="féminin">Filles</option>
        </select>
        <select value={categorie} onChange={(e) => setCategorie(e.target.value)} data-testid="effectif-filtre-categorie" className={selectCls}>
          <option value="toutes">U19 & U23</option>
          <option value="U19">U19</option>
          <option value="U23">U23</option>
        </select>
        <span className="text-xs text-[#71717A]" data-testid="effectif-compteur">{filtres.length} joueur(s)</span>
        <span className="text-xs text-[#C5A059]" data-testid="effectif-compteur-etoiles">
          ★ {regs.filter((r) => r.selection_etoiles).length} retenu(s) Étoiles
        </span>
        <span className="flex-1" />
        <button
          onClick={remplir}
          data-testid="effectif-remplir"
          title="Assigne tous les joueurs confirmés dans les équipes (poste principal puis secondaire)"
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-2 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
        >
          <Wand2 size={12} /> Remplir les équipes
        </button>
      </div>

      {/* Liste */}
      <div className="border border-[#27272A] divide-y divide-[#27272A]">
        {filtres.length === 0 && <p className="px-5 py-10 text-center text-[#71717A] text-sm">Aucun joueur ne correspond aux filtres.</p>}
        {filtres.map((r) => {
          const team = equipeDe[r.id];
          const cat = (r.categorie_key || "").startsWith("u19") ? "U19" : "U23";
          const payee = r.paiement_statut === "payée";
          const ciblesPossibles = teams.filter((t) => t.id !== team?.id && (t.genre || "masculin") === (r.genre || "masculin") && (t.categorie || "U23") === cat);
          const slotOptions = [];
          if (r.poste_principal) slotOptions.push({ value: r.poste_principal, label: `Principal : ${r.poste_principal}` });
          if (r.poste_secondaire) slotOptions.push({ value: r.poste_secondaire, label: `Secondaire : ${r.poste_secondaire}` });
          return (
            <div key={r.id} data-testid={`effectif-row-${r.id.slice(0, 8)}`} className="px-5 py-3.5 flex flex-wrap items-center gap-3 text-sm">
              <div className="min-w-[190px]">
                <p className="font-medium">{r.prenom} {r.nom}</p>
                <p className="text-[11px] text-[#71717A]">{r.reference} · {r.poste_principal}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#27272A] text-[#A1A1AA]">
                {(r.genre || "masculin") === "féminin" ? "Fille" : "Garçon"} · {cat}
              </span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${payee ? "border-[#C5A059]/50 text-[#C5A059]" : "border-[#27272A] text-[#71717A]"}`}>
                {payee ? "Confirmé" : "Non payé"}
              </span>
              <span className={`text-xs ${team ? "text-emerald-300" : "text-[#71717A]"}`} data-testid={`effectif-equipe-${r.id.slice(0, 8)}`}>
                {team ? team.nom : "Sans équipe"}
              </span>
              <span className="flex-1" />
              <button
                onClick={() => toggleEtoile(r)}
                data-testid={`effectif-etoile-${r.id.slice(0, 8)}`}
                title={r.selection_etoiles ? "Retiré de la sélection Étoiles" : "Retenir pour le Tournoi des Étoiles (21 nov)"}
                className={`p-1.5 transition-colors ${r.selection_etoiles ? "text-[#C5A059]" : "text-[#3f3f46] hover:text-[#C5A059]"}`}
              >
                <Star size={15} fill={r.selection_etoiles ? "#C5A059" : "none"} />
              </button>
              {payee && (
                <>
                  {slotOptions.length > 1 && (
                    <select
                      value={slots[r.id] || ""}
                      onChange={(e) => setSlots({ ...slots, [r.id]: e.target.value })}
                      data-testid={`effectif-slot-${r.id.slice(0, 8)}`}
                      className={selectCls}
                      title="Choisir le poste (principal ou secondaire)"
                    >
                      <option value="">Poste auto</option>
                      {slotOptions.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  )}
                  <select
                    value={cibles[r.id] || ""}
                    onChange={(e) => setCibles({ ...cibles, [r.id]: e.target.value })}
                    data-testid={`effectif-select-${r.id.slice(0, 8)}`}
                    className={selectCls}
                  >
                    <option value="" disabled>Équipe cible…</option>
                    {ciblesPossibles.map((t) => {
                      const label = `${t.nom} (${t.joueurs.length}/11)`;
                      return <option key={t.id} value={t.id}>{label}</option>;
                    })}
                  </select>
                  <button
                    onClick={() => placer(r)}
                    disabled={!cibles[r.id]}
                    data-testid={`effectif-placer-${r.id.slice(0, 8)}`}
                    title={team ? "Changer d'équipe" : "Placer dans une équipe"}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ArrowRightLeft size={11} /> {team ? "Changer" : "Placer"}
                  </button>
                  {team && (
                    <button
                      onClick={() => dupliquer(r)}
                      disabled={!cibles[r.id]}
                      data-testid={`effectif-dupliquer-${r.id.slice(0, 8)}`}
                      title="Ajouter aussi dans l'équipe cible (sans le retirer de la sienne)"
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-sky-400/50 text-sky-300 hover:bg-sky-400 hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Copy size={11} /> Dupliquer
                    </button>
                  )}
                </>
              )}
              {team && (
                <button
                  onClick={() => retirer(r)}
                  data-testid={`effectif-retirer-${r.id.slice(0, 8)}`}
                  title="Retirer de l'équipe (inscription conservée)"
                  className="p-1.5 text-[#71717A] hover:text-red-400 transition-colors"
                >
                  <UserMinus size={15} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
